import {
    createDisposer,
    createLogPrefix,
    type Disposable,
    getUserAgentInfo,
    globalRef,
    logDebug,
    logWarn,
    type LogTarget,
    RequestError,
    requestWithRetry,
} from '@amazon/vinyl-util'
import { AuthStatus } from '../env/auth'
import type {
    KeyValueStore,
    LifecycleSource,
    NetworkSource,
    PlaybackAuth,
    PlaybackDeviceProfile,
    PlaybackEndpoints,
    PlaybackViewport,
    SessionContext,
} from '../env/PlaybackEnv'
import { foregroundSessionDetails } from './foregroundSession'
import {
    type MetrixAction,
    type MetrixActionQueue,
    toAdditionalDetails,
    type WireDetails,
} from './metrixAction'

// The MusicMetrix (AMTC) uploader every metric family shares: batches each action
// onto the wire contract GH's `@amzn/music-metrix-sdk-typescript` `HttpReporter`
// reports, then POSTs it. Emission is observational: `enqueue` never throws. As Morpho's
// `CirrusMetricReporter`, every action is stored until it is sent, so one recorded offline,
// signed out of a token session or before a restart is sent later, and a failed upload is
// stored again unless the service rejected it (400).

const DEFAULT_BATCH_SIZE = 20

// Morpho's `kMaxEventsPerBatch`: what one request carries when a backlog drains.
const MAX_UPLOAD_SIZE = 100

// The mobile spec's cap on unsent events (HR-METRICS-TRANSPORT AC-85); the oldest go first.
const MAX_STORED = 1000

// GH's value until the metrics owners settle amu's own (Blackbolt's Cradle profiles count only BLACKBOLT).
export const METRIX_PRODUCT_VERSION = 'GREENHORNET'

export const METRIX_PRODUCT_ID_WEB_PLAYER = '803'

const DEVICE_LOCALE = /^[a-z]{2}-[A-Z]{2}$/

// The platform locale, as the SDK's LocaleCollector reads it; the app's display language is `appLocale`.
function deviceLocale(language: string): string {
    return DEVICE_LOCALE.test(language) ? language.replace('-', '_') : 'en_US'
}

function deviceProperties(viewport: PlaybackViewport | undefined): string | undefined {
    if (!viewport) return undefined
    const { width, height } = viewport
    return JSON.stringify({
        displayWidthPixels: width,
        displayHeightPixels: height,
        viewType: width > height ? 'LANDSCAPE' : 'PORTRAIT',
    })
}

/**
 * What GH's Metrix SDK stamps on every action of every family: its `globalAttributes`
 * and the web collectors' `locale`, `userAgent` and `deviceProperties`. They override
 * the action's own, as the SDK's merge does.
 */
function metrixGlobalDetails(env: MetrixUploaderEnv): WireDetails {
    const { locale, appVersion, viewport } = env.device
    return {
        locale: deviceLocale(locale),
        productId: METRIX_PRODUCT_ID_WEB_PLAYER,
        productVersion: METRIX_PRODUCT_VERSION,
        productVersionDetail: appVersion,
        userAgent: getUserAgentInfo().userAgent || undefined,
        deviceProperties: deviceProperties(viewport),
    }
}

/** One row of the AMTC `clientActionList` (the SDK's `ClientAction`). */
interface ClientAction extends MetrixAction {
    readonly contentInfo: readonly Record<string, string>[]
}

/**
 * The AMTC ingestion payload — a SINGLE object per batch (the SDK's
 * `FormattedMetrixPayload`), NOT an array. Identity fields are top-level and the
 * per-event rows live under `clientActionList`.
 */
interface FormattedMetrixPayload {
    readonly customerId?: string
    readonly deviceId: string
    readonly deviceType: string
    readonly clientActionList: readonly ClientAction[]
}

interface RequestAuth {
    readonly headers: Record<string, string>
    readonly credentials?: RequestCredentials
    readonly customerId?: string
    readonly deviceId: string
    readonly deviceType: string
}

/** What an uploader reads from its host; a whole `PlaybackEnv` is one. */
export interface MetrixUploaderEnv {
    readonly auth: Pick<
        PlaybackAuth,
        | 'status$'
        | 'isSignedIn'
        | 'authTokenProvider'
        | 'usesBearer'
        | 'requestCredentials'
        | 'getCsrfHeaders'
        | 'getCustomerId'
    >
    readonly device: Pick<
        PlaybackDeviceProfile,
        'deviceId' | 'deviceTypeId' | 'appVersion' | 'locale' | 'viewport'
    >
    readonly endpoints: Pick<PlaybackEndpoints, 'canUploadMetrix' | 'metrixEventsUrl' | 'pageUrl'>
    readonly lifecycle: LifecycleSource
    readonly network: NetworkSource
    readonly store: KeyValueStore
    readonly session: Pick<SessionContext, 'foregroundSessionId$'>
}

export interface MetrixUploaderOptions {
    /** The `store` key the unsent actions are kept under, one per uploader. */
    readonly storageKey: string
    readonly batchSize?: number
}

const AWAITING_SIGN_IN = new Error('a token session uploads once signed in')

const liveUploadersRef = globalRef(() => new Set<MetrixUploader>())

/**
 * Uploads what every live uploader holds and resolves once all their uploads settle, for a
 * navigation that ends the session (a cookie sign-out) before the page-hide flush could send it.
 * Never rejects: a failed upload is logged and dropped.
 */
export async function flushMetrixUploaders(): Promise<void> {
    await Promise.all([...liveUploadersRef.value].map((uploader) => uploader.flushAndSettle()))
}

export class MetrixUploader implements MetrixActionQueue, LogTarget, Disposable {
    logPrefix = createLogPrefix('MetrixUploader')

    private readonly buffer: MetrixAction[]
    private readonly uploads = new Set<Promise<void>>()
    private readonly disposer = createDisposer()
    private readonly storageKey: string
    private readonly batchSize: number
    private pageHidden = false
    private watchingSignIn = false

    constructor(
        private readonly env: MetrixUploaderEnv,
        { storageKey, batchSize = DEFAULT_BATCH_SIZE }: MetrixUploaderOptions
    ) {
        this.storageKey = storageKey
        this.batchSize = batchSize
        this.buffer = env.endpoints.canUploadMetrix() ? this.restore() : this.discard()
        const { lifecycle, network } = env
        const liveUploaders = liveUploadersRef.value
        liveUploaders.add(this)
        this.disposer.add(() => liveUploaders.delete(this))
        // Last chance to ship a sub-threshold buffer. Both signals are needed:
        // terminate covers unload/bfcache, hidden covers tab-switch.
        this.disposer.add(
            lifecycle.onTerminate(() => {
                this.pageHidden = true
                this.flush({ keepalive: true })
            })
        )
        this.disposer.add(
            lifecycle.onResume(() => {
                this.pageHidden = false
            })
        )
        this.disposer.add(
            lifecycle.hidden$.onData((hidden) => {
                if (hidden) this.flush({ keepalive: true })
            })
        )
        this.disposer.add(
            network.online$.onData((online, previous) => {
                if (online && previous === false) this.flush()
            })
        )
        this.flush()
    }

    enqueue(action: MetrixAction): void {
        if (!this.env.endpoints.canUploadMetrix()) return
        this.buffer.push({
            ...action,
            additionalDetails: {
                // As GH's `logEvent`: a session the action carries itself wins.
                ...foregroundSessionDetails(this.env.session.foregroundSessionId$.value),
                ...action.additionalDetails,
                ...toAdditionalDetails(metrixGlobalDetails(this.env)),
            },
        })
        this.store()
        // Past the page-hide flush nothing else would ship a late event, e.g. an unload's systemStop.
        if (this.pageHidden) this.flush({ keepalive: true })
        else if (this.buffer.length >= this.batchSize) this.flush()
    }

    /**
     * Sends what is buffered; offline, or signed out of a token session, it stays stored until the
     * network returns or the session signs in.
     */
    flush(options?: { readonly keepalive?: boolean }): void {
        if (!this.env.network.online$.value) return
        while (this.buffer.length > 0) {
            const batch = this.buffer.splice(0, MAX_UPLOAD_SIZE)
            this.store()
            const upload: Promise<void> = this.upload(batch, options?.keepalive ?? false)
                .catch((error: unknown) => this.uploadFailed(batch, error))
                .finally(() => this.uploads.delete(upload))
            this.uploads.add(upload)
        }
    }

    /** Uploads the buffer and resolves once every upload in flight has settled. */
    async flushAndSettle(): Promise<void> {
        this.flush({ keepalive: true })
        await Promise.all(this.uploads)
    }

    /** What it holds stays stored, for the next uploader on the same key to send. */
    dispose(): void {
        this.disposer.dispose()
    }

    // Armed by the first upload a token session could not send, so that sign-in sends it.
    private flushOnSignIn(): void {
        if (this.watchingSignIn) return
        this.watchingSignIn = true
        this.disposer.add(
            this.env.auth.status$.onData((status, previous) => {
                if (status === AuthStatus.SIGNED_IN && previous !== undefined) this.flush()
            })
        )
    }

    private uploadFailed(batch: readonly MetrixAction[], error: unknown): void {
        if (error !== AWAITING_SIGN_IN) {
            const rejected = error instanceof RequestError && error.response?.status === 400
            logWarn(
                this,
                rejected ? 'upload rejected, dropped' : 'upload failed, kept for the next upload',
                error instanceof Error ? error.message : String(error)
            )
            if (rejected) return
        }
        this.buffer.unshift(...batch)
        this.store()
    }

    private restore(): MetrixAction[] {
        try {
            const stored = this.env.store.getItem(this.storageKey)
            return stored ? (JSON.parse(stored) as MetrixAction[]) : []
        } catch (error) {
            logWarn(this, 'stored actions unreadable, dropped', error)
            return []
        }
    }

    private discard(): MetrixAction[] {
        try {
            this.env.store.removeItem(this.storageKey)
        } catch (error) {
            logWarn(this, 'discarding unsent actions failed', error)
        }
        return []
    }

    private store(): void {
        this.buffer.splice(0, this.buffer.length - MAX_STORED)
        try {
            if (this.buffer.length === 0) this.env.store.removeItem(this.storageKey)
            else this.env.store.setItem(this.storageKey, JSON.stringify(this.buffer))
        } catch (error) {
            logWarn(this, 'storing unsent actions failed', error)
        }
    }

    private async upload(batch: readonly MetrixAction[], keepalive: boolean): Promise<void> {
        const session = this.env.auth
        if (session.usesBearer() && !session.isSignedIn()) {
            this.flushOnSignIn()
            throw AWAITING_SIGN_IN
        }
        const auth = await buildRequestAuth(this.env)
        const payload: FormattedMetrixPayload = {
            ...(auth.customerId ? { customerId: auth.customerId } : null),
            deviceId: auth.deviceId,
            deviceType: auth.deviceType,
            clientActionList: batch.map((action) => ({
                ...action,
                contentInfo: action.contentInfo ?? [],
            })),
        }
        logDebug(this, 'upload', { count: batch.length, keepalive })
        await requestWithRetry(this.env.endpoints.metrixEventsUrl(), {
            method: 'POST',
            headers: auth.headers,
            ...(auth.credentials ? { credentials: auth.credentials } : null),
            // keepalive lets an in-flight page-hide POST outlive the document.
            ...(keepalive ? { keepalive: true } : null),
            body: JSON.stringify(payload),
        })
    }
}

/**
 * The ingestion request's auth headers and identity, as GH's `getMetricsAuthData` hands them to
 * the Metrix SDK: a token session sends its access token (`x-amz-access-token`), a cookie session
 * the CSRF triple with `credentials: 'include'` for its session cookie; both send the device
 * headers. Auth and CSRF stay out of the POST body, where GH once leaked them.
 */
async function buildRequestAuth({
    auth,
    device,
    endpoints,
}: MetrixUploaderEnv): Promise<RequestAuth> {
    const { deviceId, deviceTypeId } = device

    const headers: Record<string, string> = {
        'content-type': 'application/json',
        // AMTC ingestion reads `x-amz-*` device headers (the Metrix SDK's
        // `AuthHeaders`), NOT the `x-amzn-*` spelling Firefly `gql.ts` sends.
        'x-amz-device-id': deviceId,
        'x-amz-device-type': deviceTypeId,
        ...auth.getCsrfHeaders(),
    }
    if (auth.usesBearer()) headers['x-amz-access-token'] = await auth.authTokenProvider()

    const customerId = auth.getCustomerId() ?? undefined
    if (customerId) headers['x-amz-customer-id'] = customerId
    const pageUrl = endpoints.pageUrl()
    if (pageUrl) headers['x-amz-request-url'] = pageUrl

    const credentials = auth.requestCredentials
    return {
        headers,
        ...(credentials ? { credentials } : null),
        ...(customerId ? { customerId } : null),
        deviceId,
        deviceType: deviceTypeId,
    }
}
